<template>
  <div v-if="showRemoveCardDetails">
    <square-payments-response-modals
      v-if="!disableResponseModals"
      :success-component="successComponent"
      @closed="completeModalClose"
    />

    <b-modal
      v-model="showModal"
      no-close-on-esc
      no-close-on-backdrop
      hide-header-close
      static
    >
      <template v-slot:modal-title>
        <span class="icon-container">
          <font-awesome-icon
            :icon="[ 'fa', 'triangle-exclamation' ]"
            size="2x"
            class="icon"
            fixed-width
            color="#ffcc00"
          />
        </span>
        Remove stored credit card details
      </template>
      <template v-slot:modal-footer>
        <b-button
          variant="primary"
          :disabled="removeProcessing"
          @click="(val) => $emit('cancel')"
        >
          Cancel
        </b-button>
        <b-button
          variant="danger"
          :disabled="removeProcessing"
          @click="removeCardConfirm"
        >
          Remove stored card
        </b-button>
      </template>
      <loading-spinner
        v-if="removeProcessing && !requestProcessed"
        class="removing-card-processing"
      >
        <template v-slot:title>
          Removing credit card details.
        </template>
        Credit card details being removed. Please wait...
      </loading-spinner>
      <div
        v-if="!removeProcessing && !requestProcessed"
      >
        <p>
          Do you wish to remove this stored credit card ?
        </p>
        <square-payments-storedcard-display
          :account="account"
          :card-details="removeCardDetails"
          size="md"
        />
        <p class="warning-message">
          This action cannot be undone.
          Once removed you will need to re-enter these details again to continue using this card.
        </p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core'
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons'
import SquarePaymentResponseModals from '../modals/response-modals.vue'
import SquarePaymentsMixin from '../mixins/square-payments'
import RemoveCardSuccessContent from '../modals/content/removecard-success.vue'

library.add(faExclamationTriangle)

export default {
  name: 'square-payments-remove-stored-card-modal',
  components: {
    'square-payments-response-modals': SquarePaymentResponseModals
  },
  mixins: [
    SquarePaymentsMixin
  ],
  props: {
    account: {
      type: Object,
      required: true
    },
    removeCardDetails: {
      type: Object,
      required: true
    },
    disableResponseModals: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data: function() {
    return {
      removeProcessing: false,
      processingStart: 0
    }
  },
  computed: {
    userid: function() {
      return this.account.userid
    },
    successComponent: function() {
      return RemoveCardSuccessContent
    },
    showModal: function() {
      return this.showRemoveCardDetails && !this.requestProcessed
    },
    skipConfirmation: function() {
      return this.removeCardDetails.skipConfirmation
    },
    showRemoveCardDetails: {
      set: function(val) {
        // No-Op
      },
      get: function() {
        return !!this.removeCardDetails
      }
    },
    requestProcessed: function() {
      return this.processingStart > 0
    }
  },
  mounted: function() {
    if (this.skipConfirmation) {
      this.removeCardConfirm()
    }
  },
  methods: {
    completeModalClose: function() {
      if (this.retryableError) {
        this.retryableError = false
        this.$store.commit('square/PROCESSING_RESULT')
        this.$emit('error', null)
      }
      else {
        // Only emit success/completed after the success modal is closed
        const result = this.$store.getters['square/processingResult']
        if (result && result.status) {
          this.$emit('success', this.removeCardDetails)
        }
        this.$emit('completed', result)
      }
    },
    delayResponse: function(response, data) {
      const delay = 2000 - (new Date().getTime() - this.processingStart)
      setTimeout(() => {
        this.removeProcessing = false

        if (this.disableResponseModals) {
          // When response modals are disabled, emit the event directly
          if (response === 'success') {
            this.$emit('success', this.removeCardDetails)
          }
          else if (response === 'error') {
            this.$emit('error', data)
          }
          // Reset processing state for next operation
          this.processingStart = 0
        }
        else {
          // Original behavior with modals
          if (response === 'error') {
            this.$emit(response, data)
          }
          // For success, the modal will be shown and completeModalClose will handle the success emission
        }
      }, delay)
    },
    removeCardConfirm: function() {
      this.processingStart = new Date().getTime()
      this.removeProcessing = true
      this.$store.dispatch('square/removeStoredCard', {
        userid: this.userid,
        cardid: this.removeCardDetails.id
      })
        .then(_ => {
          const processingResult = this.$store.getters['square/processingResult']
          this.$store.commit('square/PROCESSING_RESULT', { type: 'card', ...processingResult })
          // Don't emit 'removed' here - wait for the success modal to be closed
          this.delayResponse('success', this.removeCardDetails)
        })
        .catch(err => {
          const processingResult = this.$store.getters['square/processingResult']
          this.$store.commit('square/PROCESSING_RESULT', { type: 'card', ...processingResult })
          this.delayResponse('error', new Error(`Error removing stored card details: ${err.message}`))
        })
    }
  }
}
</script>

<style>

</style>
